import React, { useCallback, useEffect, useRef } from 'react';

import { updatePageLoadInteractionName } from '@atlaskit/react-ufo/interaction-metrics';
import { updatePageloadName } from '@atlaskit/react-ufo/update-pageload-name';
import { token } from '@atlaskit/tokens';

import { maybeParseNativeEmbedParams } from '@atlassian/native-embeds-core/services/client/iframe-integration/main';

import { EmbeddedConfluenceTrackingContext } from '@confluence/browser-metrics/entry-points/EmbeddedConfluenceTrackingContext.ts';
import { useEmbeddedViewPageFeatures } from '@confluence/embedded-confluence-allowed-features/entry-points/view-page.ts';
import { usePageNativeEmbedClient } from '@confluence/embedded-confluence-allowed-features/entry-points/usePageNativeEmbedClient.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import type { BaseViewPageProps } from '@confluence/view-page-common/entry-points/BaseViewPageProps.ts';
import { useViewPagePersistentScrollPosition } from '@confluence/view-page-common/entry-points/useViewPagePersistentScrollPosition.ts';
import { StartViewPageLoad } from '@confluence/view-page-common/entry-points/perf.config.ts';
import { ViewPageCommon } from '@confluence/view-page-common/entry-points/ViewPageCommon.ts';
import { useEventHandlers } from '@confluence/adf-renderer/entry-points/useEventHandlers.ts';
import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';

import { useTrackingConfig } from '../embedded-common/useTrackingConfig.ts';
import { usePageTheme } from '../embedded-common/usePageTheme.ts';
import { usePropsUpdatedFrequencyMonitor } from '../embedded-common/usePropsUpdatedFrequencyMonitor.ts';

import { HeaderContainer } from './HeaderContainer.tsx';
import { useSyncEditButtonPermissionWithHost } from './useSyncEditButtonPermissionWithHost.ts';

// For native embeds, the renderer and title components own their own 72px gutters,
// so the outer container padding is zeroed out.
// The cover-picture CSS variables are also neutralised (to 0px / 100%) so that
// the cover image remains edge-to-edge against a zero-padded container.
// The default minHeight is overridden from calc(100vh - 56px) inherited from ContentScreenComponent to
// break a circular dependency where the iframe infinitely grows.
const NATIVE_EMBED_CONTENT_SCREEN_STYLES = {
	paddingTop: '0px',
	paddingBottom: '0px',
	paddingLeft: '0px',
	paddingRight: '0px',
	'--full-cover-image-margin': '0px',
	'--full-cover-image-width': '100%',
	minHeight: 'auto',
};

// For non-native embeds (e.g. Jira preview panel), keep the original padding
// so the content is not compressed against the panel edges.
const CONTENT_SCREEN_STYLES: React.CSSProperties = {
	padding: `0px ${token('space.200')} 0px 0px`,
	minHeight: 'auto',
};

type EmbeddedViewPageProps = BaseViewPageProps & {
	ClassicEditorContextProviderLoader?: React.ComponentType<any>;
	showHeaderButtons?: boolean;
};

export const EmbeddedViewPage: React.FC<EmbeddedViewPageProps> = ({
	contentId,
	spaceKey,
	ClassicEditorContextProviderLoader,
	showHeaderButtons = true, // for backwards compatibility
}) => {
	usePageTheme();
	const { setReadPageContent } = usePageNativeEmbedClient();
	const currentPageAdfRef = useRef<{ contentId: string; pageAdf: string | null }>({
		contentId,
		pageAdf: null,
	});

	const handleCurrentContentBodyDynamicChange = useCallback<
		NonNullable<React.ComponentProps<typeof ViewPageCommon>['onCurrentContentBodyDynamicChange']>
	>(
		(contentBodyDynamic, currentContentId) => {
			currentPageAdfRef.current = {
				contentId: currentContentId ?? contentId,
				pageAdf:
					currentContentId === contentId &&
					contentBodyDynamic?.representation === 'atlas_doc_format'
						? (contentBodyDynamic.value ?? '')
						: '',
			};
		},
		[contentId],
	);

	useEffect(() => {
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		if (fg('maui_remixable_page_native_embeds')) {
			setReadPageContent({
				contentId,
				source: 'renderer',
				getPageAdf: () => {
					const currentPageAdf = currentPageAdfRef.current;
					return currentPageAdf.contentId === contentId ? (currentPageAdf.pageAdf ?? '') : '';
				},
			});
		}
	}, [contentId, setReadPageContent]);

	usePropsUpdatedFrequencyMonitor('view');

	// Rename the UFO experience from 'view_page' to 'embedded-view-page' so that
	// embedded view page loads are reported as a distinct UFO experience.
	// Both calls are needed because there are two UFO tracking systems (trace-based
	// and legacy interaction-metrics) and we need to rename in whichever is active.
	// Skip when the page is live — LivePageComponent handles renaming to
	// 'embedded-live-edit' and this effect (parent) runs after the child's,
	// so it would overwrite the live-edit name.
	const isLivePage = useIsCurrentPageLive();
	useEffect(() => {
		if (!isLivePage) {
			updatePageloadName('embedded-view-page');
			updatePageLoadInteractionName('embedded-view-page');
		}
	}, [isLivePage]);

	const {
		hasByLineContributors,
		hasByLineExtensions,
		hasComments,
		hasInlineCommentsAndHighlightActions,
		hasPageReactions,
		hasStickyHeader,
		showDelete,
		showEdit,
		showShare,
		includeNonLicensedUsersToShare,
	} = useEmbeddedViewPageFeatures();
	const customTrackingConfig = useTrackingConfig();

	const eventHandlers = useEventHandlers();

	useViewPagePersistentScrollPosition(contentId, 'EP');

	const isNativeEmbed = !!maybeParseNativeEmbedParams(new URLSearchParams(window.location.search));

	useSyncEditButtonPermissionWithHost({
		contentId,
		enabled:
			isNativeEmbed &&
			(expValEquals('cc-maui-experiment', 'isEnabled', true) ||
				// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
				fg('platform_native_embeds_rollout_non_maui_experience')),
		phase2Enabled:
			// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
			fg('cc-maui-phase-2') || fg('platform_native_embeds_rollout_non_maui_experience'),
	});

	return (
		<EmbeddedConfluenceTrackingContext.Provider value>
			<StartViewPageLoad contentId={contentId} customConfig={customTrackingConfig} />
			<ViewPageCommon
				contentId={contentId}
				contentScreenStyles={
					isNativeEmbed ? NATIVE_EMBED_CONTENT_SCREEN_STYLES : CONTENT_SCREEN_STYLES
				}
				eventHandlers={eventHandlers}
				hasSatisfactionSurveys={false}
				hasEOPRecs={false}
				hasInlineComments={hasInlineCommentsAndHighlightActions}
				hasLabels={false}
				hasReactions={hasPageReactions}
				hasPageComments={hasComments}
				hasInlineActions={hasInlineCommentsAndHighlightActions}
				spaceKey={spaceKey}
				isEmbeddedPage
				onCurrentContentBodyDynamicChange={
					// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
					fg('maui_remixable_page_native_embeds')
						? handleCurrentContentBodyDynamicChange
						: undefined
				}
				titleAndByLineOptions={{
					hasByLineContributors,
					hasByLineExtensions,
					headingContainer: !(
						isNativeEmbed &&
						(expValEquals('cc-maui-experiment', 'isEnabled', true) ||
							// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
							fg('platform_native_embeds_rollout_non_maui_experience'))
					)
						? ({ children, showInlineCommentButton, hasCoverPicture }) => (
								<HeaderContainer
									contentId={contentId}
									showInlineCommentButton={showHeaderButtons && showInlineCommentButton}
									hasInlineComments={hasInlineCommentsAndHighlightActions}
									hasStickyHeader={hasStickyHeader}
									spaceKey={spaceKey}
									showDelete={showHeaderButtons && showDelete}
									showEdit={showHeaderButtons && showEdit}
									showShare={showHeaderButtons && showShare}
									includeNonLicensedUsersToShare={includeNonLicensedUsersToShare}
									hasCoverPicture={hasCoverPicture}
								>
									{children}
								</HeaderContainer>
							)
						: undefined,
				}}
				ClassicEditorContextProviderLoader={ClassicEditorContextProviderLoader}
			/>
		</EmbeddedConfluenceTrackingContext.Provider>
	);
};
